import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { attendanceService } from '../services/attendanceService';
import { nekoStaff } from 'public-contracts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Calendar,
  Clock,
  Download,
  Filter,
  Search,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  MapPin,
  List,
  Grid3X3,
  CalendarIcon,
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

type AttendanceRecord = nekoStaff.AttendanceRecord;

interface AttendanceStats {
  totalDays: number;
  presentDays: number;
  absentDays: number;
  lateDays: number;
  totalHours: number;
  averageHours: number;
}

const ModernAttendanceRecords: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user, fetchUser } = useUserStore();
  const navigate = useNavigate();

  const [attendanceRecords, setAttendanceRecords] = useState<
    AttendanceRecord[]
  >([]);
  const [filteredRecords, setFilteredRecords] = useState<AttendanceRecord[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  const [calendarView, setCalendarView] = useState<
    'day' | 'week' | 'month' | 'year'
  >('month');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentDay, setCurrentDay] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [currentYear, setCurrentYear] = useState(new Date());
  const [stats, setStats] = useState<AttendanceStats>({
    totalDays: 0,
    presentDays: 0,
    absentDays: 0,
    lateDays: 0,
    totalHours: 0,
    averageHours: 0,
  });

  // Ensure user data is loaded
  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  // Load attendance records
  useEffect(() => {
    if (user?.id) {
      fetchAttendanceRecords();
    }
  }, [user?.id]);

  // Filter records when search term or filters change
  useEffect(() => {
    let filtered = attendanceRecords;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (record) =>
          record.date.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.userId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by date
    if (dateFilter) {
      filtered = filtered.filter((record) =>
        record.date.startsWith(dateFilter)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter((record) => {
        if (statusFilter === 'present') return record.isIn;
        if (statusFilter === 'absent') return !record.isIn;
        return true;
      });
    }

    setFilteredRecords(filtered);
  }, [attendanceRecords, searchTerm, dateFilter, statusFilter]);

  const fetchAttendanceRecords = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        setError('User not found');
        return;
      }

      const records = await attendanceService.getAttendanceRecordsByUserId(
        String(user.id)
      );
      setAttendanceRecords(records);
      calculateStats(records);
    } catch (err) {
      console.error('Error fetching attendance records:', err);
      // Don't show error notification, just set empty records
      setAttendanceRecords([]);
      calculateStats([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (records: AttendanceRecord[]) => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const recentRecords = records.filter(
      (record) => new Date(record.date) >= thirtyDaysAgo
    );

    const presentDays = recentRecords.filter((record) => record.isIn).length;
    const totalDays = recentRecords.length;
    const absentDays = totalDays - presentDays;

    // Calculate total hours (simplified - assumes 8 hours per day)
    const totalHours = presentDays * 8;
    const averageHours = totalDays > 0 ? totalHours / totalDays : 0;

    setStats({
      totalDays,
      presentDays,
      absentDays,
      lateDays: 0, // Would need to implement late detection logic
      totalHours,
      averageHours,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (record: AttendanceRecord) => {
    if (record.isIn) {
      return (
        <Badge variant="default" className="bg-green-100 text-green-800">
          {translate('pos.staff.timesheet.status_present', 'Present')}
        </Badge>
      );
    } else {
      return (
        <Badge variant="secondary" className="bg-gray-100 text-gray-800">
          {translate('pos.staff.timesheet.status_absent', 'Absent')}
        </Badge>
      );
    }
  };

  const exportToCSV = () => {
    const csvContent = [
      [
        translate('pos.staff.timesheet.start_date', 'Date'),
        translate('pos.staff.attendance.checkin_time', 'Time'),
        translate('pos.staff.timesheet.status_label', 'Status'),
        translate('pos.staff.attendance.location_detected', 'Location'),
        translate('pos.common.field.notes', 'Notes'),
      ],
      ...filteredRecords.map((record) => [
        formatDate(record.date),
        formatTime(record.date),
        record.isIn
          ? translate('pos.staff.timesheet.status_present', 'Present')
          : translate('pos.staff.timesheet.status_absent', 'Absent'),
        'N/A', // Location not available in current schema
        'N/A', // Notes not available in current schema
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-records-${
      new Date().toISOString().split('T')[0]
    }.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getAttendanceForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return filteredRecords.filter((record) => record.date.startsWith(dateStr));
  };

  const getAttendanceStatusForDate = (date: Date) => {
    const records = getAttendanceForDate(date);
    if (records.length === 0) return null;

    const hasCheckIn = records.some((record) => record.isIn);
    const hasCheckOut = records.some((record) => !record.isIn);

    if (hasCheckIn && hasCheckOut) return 'complete';
    if (hasCheckIn) return 'partial';
    return 'absent';
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
  };

  // Helper functions for different calendar views
  const getWeekStart = (date: Date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay());
    return start;
  };

  const getWeekDays = (weekStart: Date) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getMonthDays = (year: number, month: number) => {
    const days = [];
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getYearMonths = (year: number) => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      months.push(new Date(year, i, 1));
    }
    return months;
  };

  const getAttendanceForDay = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return filteredRecords.filter((record) => record.date.startsWith(dateStr));
  };

  const getAttendanceForWeek = (weekStart: Date) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return filteredRecords.filter((record) => {
      const recordDate = new Date(record.date);
      return recordDate >= weekStart && recordDate <= weekEnd;
    });
  };

  const getAttendanceForMonth = (year: number, month: number) => {
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    return filteredRecords.filter((record) => {
      const recordDate = new Date(record.date);
      return recordDate >= monthStart && recordDate <= monthEnd;
    });
  };

  const getAttendanceForYear = (year: number) => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    return filteredRecords.filter((record) => {
      const recordDate = new Date(record.date);
      return recordDate >= yearStart && recordDate <= yearEnd;
    });
  };

  // Navigation functions for different views
  const navigateDay = (direction: 'prev' | 'next') => {
    const newDay = new Date(currentDay);
    if (direction === 'prev') {
      newDay.setDate(newDay.getDate() - 1);
    } else {
      newDay.setDate(newDay.getDate() + 1);
    }
    setCurrentDay(newDay);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeek = new Date(currentWeek);
    if (direction === 'prev') {
      newWeek.setDate(newWeek.getDate() - 7);
    } else {
      newWeek.setDate(newWeek.getDate() + 7);
    }
    setCurrentWeek(newWeek);
  };

  const navigateYear = (direction: 'prev' | 'next') => {
    const newYear = new Date(currentYear);
    if (direction === 'prev') {
      newYear.setFullYear(newYear.getFullYear() - 1);
    } else {
      newYear.setFullYear(newYear.getFullYear() + 1);
    }
    setCurrentYear(newYear);
  };

  if (loading) {
    return (
      <div className="space-y-6 py-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-2">
            Loading attendance records...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 py-6">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={fetchAttendanceRecords}>
            {translate('pos.common.action.refresh', 'Retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.staff.timesheet.title', 'Attendance Records')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate(
              'pos.staff.timesheet.subtitle',
              'View and manage your attendance records and timesheet'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full md:w-auto justify-start md:justify-end">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('calendar')}
            >
              <Calendar className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">
                {translate('pos.staff.timesheet.calendar_tab_label', 'Calendar')}
              </span>
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">
                {translate('pos.staff.timesheet.list_tab_label', 'List')}
              </span>
            </Button>
          </div>

          {/* Calendar View Controls (only show when in calendar mode) */}
          {viewMode === 'calendar' && (
            <div className="flex items-center border rounded-lg p-1 overflow-x-auto max-w-full">
              <Button
                variant={calendarView === 'day' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCalendarView('day')}
                className="h-8"
              >
                <CalendarIcon className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">
                  {translate('pos.staff.timesheet.view_day', 'Day')}
                </span>
              </Button>
              <Button
                variant={calendarView === 'week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCalendarView('week')}
                className="h-8"
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">
                  {translate('pos.staff.timesheet.view_week', 'Week')}
                </span>
              </Button>
              <Button
                variant={calendarView === 'month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCalendarView('month')}
                className="h-8"
              >
                <Calendar className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">
                  {translate('pos.staff.timesheet.view_month', 'Month')}
                </span>
              </Button>
              <Button
                variant={calendarView === 'year' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCalendarView('year')}
                className="h-8"
              >
                <CalendarRange className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">
                  {translate('pos.staff.timesheet.view_year', 'Year')}
                </span>
              </Button>
            </div>
          )}

          <Button onClick={exportToCSV} variant="outline" className="hidden sm:inline-flex">
            <Download className="h-4 w-4 mr-2" />
            {translate('pos.staff.timesheet.export_csv', 'Export CSV')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.staff.timesheet.stats_total_days', 'Total Days')}
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalDays}</div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.timesheet.stats_total_days_hint',
                'Last 30 days'
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate(
                'pos.staff.timesheet.stats_present_days',
                'Present Days'
              )}
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.presentDays}
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.timesheet.stats_present_days_hint',
                'Days attended'
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate(
                'pos.staff.timesheet.stats_absent_days',
                'Absent Days'
              )}
            </CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {stats.absentDays}
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.timesheet.stats_absent_days_hint',
                'Days missed'
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate(
                'pos.staff.timesheet.stats_total_hours',
                'Total Hours'
              )}
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalHours.toFixed(1)}
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.timesheet.stats_total_hours_hint',
                'Hours worked'
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {translate('pos.staff.timesheet.filters_title', 'Filters')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {translate('pos.staff.timesheet.search_label', 'Search')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={translate(
                    'pos.staff.timesheet.search_placeholder',
                    'Search records...'
                  )}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {translate('pos.staff.timesheet.date_label', 'Date')}
              </label>
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {translate('pos.staff.timesheet.status_label', 'Status')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full p-2 border rounded-md"
                aria-label="Filter by status"
              >
                <option value="all">
                  {translate('pos.staff.timesheet.status_all', 'All')}
                </option>
                <option value="present">
                  {translate('pos.staff.timesheet.status_present', 'Present')}
                </option>
                <option value="absent">
                  {translate('pos.staff.timesheet.status_absent', 'Absent')}
                </option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attendance Records */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {translate('pos.staff.timesheet.title', 'Attendance Records')} (
            {filteredRecords.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredRecords.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {translate(
                  'pos.staff.timesheet.no_records_title',
                  'No Attendance Records'
                )}
              </h3>
              <p className="text-muted-foreground mb-4">
                {attendanceRecords.length === 0
                  ? translate(
                      'pos.staff.timesheet.no_records_description',
                      'No attendance records available. Use the Check-in/Check-out tab to create your first attendance record.'
                    )
                  : translate(
                      'pos.staff.timesheet.no_filtered_records_description',
                      'No records match your current filters.'
                    )}
              </p>
              <Button
                onClick={() => {
                  navigate('/staff_profiles?tab=checkin');
                }}
              >
                {translate(
                  'pos.staff.timesheet.go_to_checkin',
                  'Go to Check-in/Check-out'
                )}
              </Button>
            </div>
          ) : viewMode === 'calendar' ? (
            // Calendar View with multiple views
            <div className="space-y-4">
              {/* Calendar Header with Navigation */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  {calendarView === 'day' &&
                    currentDay.toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  {calendarView === 'week' &&
                    `${getWeekStart(currentWeek).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })} - ${new Date(
                      getWeekStart(currentWeek).getTime() +
                        6 * 24 * 60 * 60 * 1000
                    ).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}`}
                  {calendarView === 'month' &&
                    currentMonth.toLocaleDateString(undefined, {
                      month: 'long',
                      year: 'numeric',
                    })}
                  {calendarView === 'year' &&
                    currentYear.getFullYear().toString()}
                </h3>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (calendarView === 'day') navigateDay('prev');
                      else if (calendarView === 'week') navigateWeek('prev');
                      else if (calendarView === 'month') navigateMonth('prev');
                      else if (calendarView === 'year') navigateYear('prev');
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (calendarView === 'day') setCurrentDay(new Date());
                      else if (calendarView === 'week')
                        setCurrentWeek(new Date());
                      else if (calendarView === 'month')
                        setCurrentMonth(new Date());
                      else if (calendarView === 'year')
                        setCurrentYear(new Date());
                    }}
                  >
                    {calendarView === 'day'
                      ? translate('pos.staff.timesheet.today_label', 'Today')
                      : calendarView === 'week'
                      ? translate(
                          'pos.staff.timesheet.this_week_label',
                          'This Week'
                        )
                      : calendarView === 'month'
                      ? translate(
                          'pos.staff.timesheet.this_month_label',
                          'This Month'
                        )
                      : translate(
                          'pos.staff.timesheet.this_year_label',
                          'This Year'
                        )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (calendarView === 'day') navigateDay('next');
                      else if (calendarView === 'week') navigateWeek('next');
                      else if (calendarView === 'month') navigateMonth('next');
                      else if (calendarView === 'year') navigateYear('next');
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Calendar Content based on view */}
              {calendarView === 'day' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-24 gap-1">
                    {Array.from({ length: 24 }, (_, hour) => {
                      const hourDate = new Date(currentDay);
                      hourDate.setHours(hour, 0, 0, 0);
                      const records = getAttendanceForDay(hourDate);
                      const isCurrentHour =
                        new Date().getHours() === hour &&
                        new Date().toDateString() === currentDay.toDateString();

                      return (
                        <div
                          key={hour}
                          className={`p-2 min-h-[60px] border rounded-lg ${
                            isCurrentHour
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex flex-col h-full">
                            <div className="text-xs font-medium text-muted-foreground">
                              {hour.toString().padStart(2, '0')}:00
                            </div>
                            {records.length > 0 && (
                              <div className="mt-1 space-y-1">
                                {records.map((record, recordIndex) => (
                                  <div
                                    key={recordIndex}
                                    className="text-xs p-1 rounded bg-green-100 text-green-700"
                                  >
                                    {record.isIn ? '✓ In' : '✗ Out'}{' '}
                                    {formatTime(record.date)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {calendarView === 'week' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-7 gap-1">
                    {getWeekDays(getWeekStart(currentWeek)).map(
                      (day, index) => {
                        const attendanceStatus =
                          getAttendanceStatusForDate(day);
                        const isToday =
                          day.toDateString() === new Date().toDateString();
                        const records = getAttendanceForDay(day);

                        return (
                          <div
                            key={day.toISOString()}
                            className={`p-2 min-h-[120px] border rounded-lg ${
                              isToday ? 'bg-blue-50 border-blue-200' : ''
                            } ${
                              attendanceStatus === 'complete'
                                ? 'bg-green-50 border-green-200'
                                : attendanceStatus === 'partial'
                                ? 'bg-yellow-50 border-yellow-200'
                                : attendanceStatus === 'absent'
                                ? 'bg-red-50 border-red-200'
                                : ''
                            }`}
                          >
                            <div className="flex flex-col h-full">
                              <div className="text-sm font-medium">
                                {day.toLocaleDateString(undefined, {
                                  weekday: 'short',
                                })}
                              </div>
                              <div className="text-lg font-bold">
                                {day.getDate()}
                              </div>
                              {records.length > 0 && (
                                <div className="mt-1 space-y-1">
                                  {records
                                    .slice(0, 3)
                                    .map((record, recordIndex) => (
                                      <div
                                        key={recordIndex}
                                        className="text-xs text-muted-foreground"
                                      >
                                        {record.isIn ? '✓ In' : '✗ Out'}{' '}
                                        {formatTime(record.date)}
                                      </div>
                                    ))}
                                  {records.length > 3 && (
                                    <div className="text-xs text-muted-foreground">
                                      +{records.length - 3} more
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              )}

              {calendarView === 'month' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-7 gap-1">
                    {/* Day headers */}
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                      (day) => (
                        <div
                          key={day}
                          className="p-2 text-center text-sm font-medium text-muted-foreground"
                        >
                          {day}
                        </div>
                      )
                    )}

                    {/* Calendar days */}
                    {getDaysInMonth(currentMonth).map((day, index) => {
                      if (!day) {
                        return <div key={index} className="p-2"></div>;
                      }

                      const attendanceStatus = getAttendanceStatusForDate(day);
                      const isToday =
                        day.toDateString() === new Date().toDateString();
                      const records = getAttendanceForDate(day);

                      return (
                        <div
                          key={day.toISOString()}
                          className={`
                            p-2 min-h-[60px] border rounded-lg
                            ${isToday ? 'bg-blue-50 border-blue-200' : ''}
                            ${
                              attendanceStatus === 'complete'
                                ? 'bg-green-50 border-green-200'
                                : ''
                            }
                            ${
                              attendanceStatus === 'partial'
                                ? 'bg-yellow-50 border-yellow-200'
                                : ''
                            }
                            ${
                              attendanceStatus === 'absent'
                                ? 'bg-red-50 border-red-200'
                                : ''
                            }
                          `}
                        >
                          <div className="flex flex-col h-full">
                            <div className="flex items-center justify-between">
                              <span
                                className={`text-sm font-medium ${
                                  isToday ? 'text-blue-600' : ''
                                }`}
                              >
                                {day.getDate()}
                              </span>
                              {attendanceStatus && (
                                <div className="w-2 h-2 rounded-full">
                                  {attendanceStatus === 'complete' && (
                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                  )}
                                  {attendanceStatus === 'partial' && (
                                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                  )}
                                  {attendanceStatus === 'absent' && (
                                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                  )}
                                </div>
                              )}
                            </div>
                            {records.length > 0 && (
                              <div className="mt-1 space-y-1">
                                {records
                                  .slice(0, 2)
                                  .map((record, recordIndex) => (
                                    <div
                                      key={recordIndex}
                                      className="text-xs text-muted-foreground truncate"
                                    >
                                      {record.isIn ? '✓ In' : '✗ Out'}{' '}
                                      {formatTime(record.date)}
                                    </div>
                                  ))}
                                {records.length > 2 && (
                                  <div className="text-xs text-muted-foreground">
                                    +{records.length - 2} more
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {calendarView === 'year' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    {getYearMonths(currentYear.getFullYear()).map(
                      (month, index) => {
                        const monthRecords = getAttendanceForMonth(
                          month.getFullYear(),
                          month.getMonth()
                        );
                        const presentDays = monthRecords.filter(
                          (record) => record.isIn
                        ).length;
                        const totalDays = new Date(
                          month.getFullYear(),
                          month.getMonth() + 1,
                          0
                        ).getDate();

                        return (
                          <div
                            key={month.toISOString()}
                            className="p-4 border rounded-lg bg-card"
                          >
                            <div className="text-center">
                              <div className="text-lg font-semibold">
                                {month.toLocaleDateString(undefined, {
                                  month: 'long',
                                })}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {presentDays} / {totalDays} days
                              </div>
                              <div className="mt-2">
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-green-500 h-2 rounded-full"
                                    style={{
                                      width: `${
                                        (presentDays / totalDays) * 100
                                      }%`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              )}

              {/* Legend */}
              <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>
                    {translate(
                      'pos.staff.timesheet.legend_complete',
                      'Complete'
                    )}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span>
                    {translate('pos.staff.timesheet.legend_partial', 'Partial')}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span>
                    {translate('pos.staff.timesheet.legend_absent', 'Absent')}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            // List View
            <div className="space-y-4">
              {filteredRecords.map((record) => (
                <div
                  key={record.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {formatDate(record.date)}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {formatTime(record.date)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(record)}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {/* Location not available in current schema */}
                    <div className="text-sm text-muted-foreground">
                      {translate('pos.staff.timesheet.list_user_label', 'User')}
                      : {record.userId}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernAttendanceRecords;
